// src/belediye/pages/saha/SahaPersoneliDashboardPage.js

import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchMyAssignments,
  setFilter,
  clearError,
} from "../../redux/slices/sahaAssignmentsSlice";
import { useNavigate, useParams } from "react-router-dom";

// Heroicons (outline)
import {
  UserCircleIcon,
  AdjustmentsHorizontalIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  MapPinIcon,
  DocumentTextIcon,
  PaperClipIcon,
  BuildingOffice2Icon,
  HomeModernIcon,
  HashtagIcon,
  ChevronRightIcon,
  Squares2X2Icon, // Tümü
} from "@heroicons/react/24/outline";

import TrDateTime from "../../components/utils/TrDateTime";
import { parseDateMaybe } from "../../components/utils/dateTime";
import {
  safeText,
  titleCaseTr,
  ensureFirstUpperTr,
  formatPersonNameTr,
} from "../../components/utils/textFormat";

//   Paylaşılan ev-tarzı bileşenleri (tek kaynak)
import PageHeader from "../../components/utils/PageHeader";

// FAZ-29: saha ikinci sekmesi — Kurum İçi Görevler (başvuru görevleriyle KARIŞMAZ)
import usePermissions from "../../hooks/usePermissions";
import useModuleEnabled from "../../hooks/useModuleEnabled";
import SahaKurumIciGorevler from "../../components/tasks/SahaKurumIciGorevler";
import "../../components/tasks/gorev.css";
import SearchBox from "../../components/utils/SearchBox";
import FilterSheet from "../../components/utils/FilterSheet";


import "./SahaPersoneliDashboardPage.css";
import "./SahaPersoneliDashboardPage.mobile.css";

// ------------------------------ helpers ------------------------------

const shorten = (value, n = 60) => {
  const s = safeText(value); // null/undefined/"" -> "—"
  return s.length > n ? s.slice(0, n) + "…" : s;
};


function safeParseJSON(value, fallback = {}) {
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}



/**
 * Atama tarihi için olası alanları sırayla dener.
 * Not: Saha personeli listesi "AssignmentParticipant" döndüğü için added_at genelde en güvenilir kaynaktır.
 */
function getAssignedAt(row) {
  return (
    row?.added_at || // AssignmentParticipant.added_at
    row?.assigned_at ||
    row?.assignment_detail?.assigned_at ||
    row?.assignment_assigned_at ||
    row?.created_at ||
    null
  );
}

function getApplicationCreatedAt(row) {
  return (
    row?.application_detail?.created_at ||
    row?.application_detail?.createdAt ||
    null
  );
}

function getCardThumbType(row) {
  const hasFile = Boolean(row?.application_detail?.files?.[0]);
  const hasLatLng = Boolean(
    row?.application_detail?.latitude && row?.application_detail?.longitude
  );
  if (hasFile) return "image";
  if (hasLatLng) return "location";
  return "doc";
}

function withinRange(date, from, to) {
  if (!date) return false;
  const t = date.getTime();

  if (from && t < from.getTime()) return false;

  if (to) {
    const end = new Date(to);
    end.setHours(23, 59, 59, 999);
    if (t > end.getTime()) return false;
  }

  return true;
}

function mapApplicationStatusChip(status) {
  switch (status) {
    case "çözüldü":
      return { className: "md-chip md-chip-success", label: "Çözüldü" };
    case "çözülemedi":
      return { className: "md-chip md-chip-danger", label: "Çözülemedi" };
    case "işleme_alındı":
  return { className: "md-chip md-chip-warning", label: "İncelemeye alındı" };

   
    case "yeni":
      return { className: "md-chip md-chip-info", label: "Yeni" };
    default:
      return null;
  }
}

// ------------------------------ component ------------------------------

const SahaPersoneliDashboardPage = ({ unitSlug: unitSlugProp }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const unitSlug = unitSlugProp || params.unitSlug;

  const { assignments, loading, error, filter } = useSelector(
    (state) => state.sahaAssignments
  );

  const user = useMemo(
    () => safeParseJSON(localStorage.getItem("user") || "{}", {}),
    []
  );

  // FAZ-29: görev kaynağı sekmesi — "Başvuru Görevlerim" (mevcut, PİKSEL-PARİTE) /
  // "Kurum İçi Görevlerim" (yeni modül). Sekme YALNIZ uiOn + tasks.view_assigned
  // varsa render edilir; yoksa sayfa bugünküyle birebir aynı kalır (K4/K5).
  const { uiOn, can } = usePermissions();
  const gorevModulu = useModuleEnabled("internal_tasks");
  const [gorevKaynak, setGorevKaynak] = useState("basvuru");
  const kurumSekmesiVar = uiOn && gorevModulu && can("tasks.view_assigned");

  // UI-only state (slice yapısını bozmadan)
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState("assigned_desc"); // assigned_desc | assigned_asc
  const [timePreset, setTimePreset] = useState("all"); // all | 7d | 30d | custom
  const [dateFrom, setDateFrom] = useState(""); // YYYY-MM-DD
  const [dateTo, setDateTo] = useState(""); // YYYY-MM-DD

  // Atamaları getir (mobil drawer / matchMedia mantığı FilterSheet util'ine devredildi)
  useEffect(() => {
    dispatch(fetchMyAssignments());
  }, [dispatch]);

  const stats = useMemo(() => {
    const total = assignments.length;
    const pending = assignments.filter((a) => !a.has_feedback).length;
    const done = total - pending;

    const now = new Date();
    const sevenDaysAgo = new Date(now);
    sevenDaysAgo.setDate(now.getDate() - 7);

    const assignedLast7 = assignments.filter((a) => {
      const assignedAt = parseDateMaybe(getAssignedAt(a));
      return assignedAt ? assignedAt >= sevenDaysAgo : false;
    }).length;

    return { total, pending, done, assignedLast7 };
  }, [assignments]);

  const statusFilters = useMemo(
    () => [
      { key: "all", label: `Tümü (${stats.total})`, icon: Squares2X2Icon },
      { key: "active", label: `Aktif (${stats.pending})`, icon: ClockIcon },
      { key: "past", label: `Geçmiş (${stats.done})`, icon: CheckCircleIcon },
    ],
    [stats]
  );

  const { fromDateObj, toDateObj } = useMemo(() => {
    if (timePreset === "7d" || timePreset === "30d") {
      const now = new Date();
      const from = new Date(now);
      from.setDate(now.getDate() - (timePreset === "7d" ? 7 : 30));
      return { fromDateObj: from, toDateObj: null };
    }

    if (timePreset === "custom") {
      const from = dateFrom ? new Date(dateFrom) : null;
      const to = dateTo ? new Date(dateTo) : null;
      return { fromDateObj: from, toDateObj: to };
    }

    return { fromDateObj: null, toDateObj: null };
  }, [timePreset, dateFrom, dateTo]);

  const prepared = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    // 1) status
    let list = assignments.filter((a) => {
      if (filter === "active") return !a.has_feedback;
      if (filter === "past") return a.has_feedback;
      return true;
    });

    // 2) time range (öncelik: atama tarihi; yoksa başvuru tarihi)
    if (timePreset !== "all") {
      list = list.filter((a) => {
        const assignedAt =
          parseDateMaybe(getAssignedAt(a)) ||
          parseDateMaybe(getApplicationCreatedAt(a));
        return withinRange(assignedAt, fromDateObj, toDateObj);
      });
    }

    // 3) search
    if (normalizedQuery) {
      list = list.filter((a) => {
        const subj = a?.application_detail?.subject || "";
        const desc = a?.application_detail?.description || "";
        const tracking = a?.application_detail?.tracking_number || "";
        return `${tracking} ${subj} ${desc}`
          .toLowerCase()
          .includes(normalizedQuery);
      });
    }

    // 4) sort (assignedAt desc/asc)
    list = [...list].sort((a, b) => {
      const aDate =
        parseDateMaybe(getAssignedAt(a)) ||
        parseDateMaybe(getApplicationCreatedAt(a)) ||
        new Date(0);
      const bDate =
        parseDateMaybe(getAssignedAt(b)) ||
        parseDateMaybe(getApplicationCreatedAt(b)) ||
        new Date(0);
      const diff = aDate.getTime() - bDate.getTime();
      return sortKey === "assigned_asc" ? diff : -diff;
    });


    return list;
  }, [assignments, filter, timePreset, fromDateObj, toDateObj, query, sortKey]);

  const handleCardClick = (participantId) => {
    if (!unitSlug) return;
    navigate(`/yonetim/saha/gorevlerim/${participantId}`); // FAZ-22 4b: canonical
  };

  // FilterSheet: varsayılan-dışı aktif filtre sayısı (rozet) + temizle
  const activeFilterCount =
    (timePreset !== "all" ? 1 : 0) + (sortKey !== "assigned_desc" ? 1 : 0);

  const clearFilters = () => {
    setTimePreset("all");
    setDateFrom("");
    setDateTo("");
    setSortKey("assigned_desc");
  };

  // Hooks'tan sonra erken return (rules-of-hooks uyumlu)
  if (!unitSlug) {
    return (
      <div className="page-container spd-root">
        <div className="md-card spd-inline-notice">
          <ClockIcon width={18} height={18} />
          <div>
            <div className="spd-inline-title">Birim bilgisi yükleniyor…</div>
            <div className="spd-inline-desc">Lütfen bekleyin.</div>
          </div>
        </div>
      </div>
    );
  }

  // FAZ-29: kaynak seçici (iki listeyi AYIRAN sekmeler; rozetli sayaç yok — sade)
  const kaynakSecici = kurumSekmesiVar ? (
    <div className="gk-tabs" style={{ padding: "8px 12px 0", marginBottom: 0 }} role="tablist" aria-label="Talep kaynağı">
      <button
        type="button"
        role="tab"
        aria-selected={gorevKaynak === "basvuru"}
        className={`gk-tab ${gorevKaynak === "basvuru" ? "is-active" : ""}`}
        onClick={() => setGorevKaynak("basvuru")}
      >
        Başvuru Gelen Talepler
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={gorevKaynak === "kurum"}
        className={`gk-tab ${gorevKaynak === "kurum" ? "is-active" : ""}`}
        onClick={() => setGorevKaynak("kurum")}
      >
        Kurum İçi Gelen Talepler
      </button>
    </div>
  ) : null;

  if (kurumSekmesiVar && gorevKaynak === "kurum") {
    return (
      <div className="page-container spd-root">
        {kaynakSecici}
        <SahaKurumIciGorevler />
      </div>
    );
  }

  return (
    <div className="page-container spd-root">
      {kaynakSecici}
      {/* Başlık barı — paylaşılan PageHeader: durum sekmeleri + arama + filtre */}
      <PageHeader
        title="Gelen Talepler"
        infoTitle="Gelen Talepler — Bilgi"
        infoContent={
          <div className="spd-info">
            <p>
              Size atanmış talepleri burada görürsünüz. Sekmelerle{" "}
              <strong>Aktif / Geçmiş</strong> ayırın; arama ve filtreyle daraltın.
            </p>
            <p>Bir talebe dokununca detay ve geri bildirim ekranı açılır.</p>
          </div>
        }
        tabs={
          <div className="applications-tabs" role="tablist" aria-label="Talep durumları">
            {statusFilters.map((f) => {
              const active = filter === f.key;
              const TabIcon = f.icon;
              return (
                <button
                  key={f.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  className={`applications-tab${active ? " active" : ""}`}
                  onClick={() => dispatch(setFilter(f.key))}
                >
                  <TabIcon className="applications-tab-icon" />
                  <span className="applications-tab-label">{f.label}</span>
                </button>
              );
            })}
          </div>
        }
      >
        <SearchBox
          value={query}
          onChange={setQuery}
          placeholder="Ara — takip no, konu veya açıklama"
        />

        <FilterSheet
          activeCount={activeFilterCount}
          onClear={clearFilters}
          title="Filtrele"
          label="Filtrele"
        >
          <div className="afp-section">
            <div className="afp-label">Sıralama</div>
            <select
              className="md-select"
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value)}
            >
              <option value="assigned_desc">Atama: Yeni → Eski</option>
              <option value="assigned_asc">Atama: Eski → Yeni</option>
            </select>
          </div>

          <div className="afp-section">
            <div className="afp-label">Zaman</div>
            <select
              className="md-select"
              value={timePreset}
              onChange={(e) => setTimePreset(e.target.value)}
            >
              <option value="all">Tümü</option>
              <option value="7d">Son 7 gün</option>
              <option value="30d">Son 30 gün</option>
              <option value="custom">Tarih aralığı</option>
            </select>
          </div>

          {timePreset === "custom" ? (
            <div className="afp-section">
              <div className="afp-label">Tarih aralığı</div>
              <div className="afp-dates">
                <input
                  type="date"
                  className="md-input"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
                <span className="afp-dates-sep">–</span>
                <input
                  type="date"
                  className="md-input"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </div>
            </div>
          ) : null}
        </FilterSheet>
      </PageHeader>

      {/* Karşılama + özet istatistikler */}
      <section className="md-card spd-welcome">
        <div className="spd-welcome-head">
          <div className="spd-welcome-title">
            <UserCircleIcon width={26} height={26} className="spd-welcome-icon" />
            <div className="spd-welcome-text">
              <div className="spd-welcome-hello">
                Hoş geldiniz, {formatPersonNameTr(user)}
              </div>
              <div className="spd-welcome-sub">
                Size atanmış taleplerin genel özeti ve atama listesi.
              </div>
            </div>
          </div>
        </div>

            <div className="spd-stats">
              <div className="spd-stat">
                <ClockIcon width={18} height={18} className="spd-stat-icon" />
                <div className="spd-stat-meta">
                  <div className="spd-stat-label">Bekleyen</div>
                  <div className="spd-stat-value">{stats.pending}</div>
                </div>
              </div>

              <div className="spd-stat">
                <CheckCircleIcon
                  width={18}
                  height={18}
                  className="spd-stat-icon"
                />
                <div className="spd-stat-meta">
                  <div className="spd-stat-label">Tamamlanan</div>
                  <div className="spd-stat-value">{stats.done}</div>
                </div>
              </div>

              <div className="spd-stat">
                <CalendarDaysIcon
                  width={18}
                  height={18}
                  className="spd-stat-icon"
                />
                <div className="spd-stat-meta">
                  <div className="spd-stat-label">Son 7 gün</div>
                  <div className="spd-stat-value">{stats.assignedLast7}</div>
                </div>
              </div>
            </div>
          </section>

          {/* States */}
            {error ? (
              <section className="md-card spd-state spd-state--error">
                <div className="spd-state-row">
                  <ExclamationTriangleIcon width={18} height={18} />
                  <div className="spd-state-body">
                    <div className="spd-state-title">Hata</div>
                    <div className="spd-state-text">{error}</div>
                  </div>
                  <button
                    type="button"
                    className="btn-md btn-md-ghost"
                    onClick={() => dispatch(clearError())}
                  >
                    Kapat
                  </button>
                </div>
              </section>
            ) : null}

            {loading ? (
              <section className="md-card spd-state spd-state--loading">
                <div className="spd-state-row">
                  <ClockIcon width={18} height={18} />
                  <div className="spd-state-text">Yükleniyor…</div>
                </div>
              </section>
            ) : null}

            {!loading && prepared.length === 0 ? (
              <section className="md-card spd-state spd-state--empty">
                <div className="spd-state-row">
                  <AdjustmentsHorizontalIcon width={18} height={18} />
                  <div className="spd-state-body">
                    <div className="spd-state-title">Talep bulunamadı</div>
                    <div className="spd-state-text">
                      Seçtiğiniz filtrelere uygun talep yok.
                    </div>
                  </div>
                </div>
              </section>
            ) : null}

            {/* Atamalar başlığı */}
            <div className="spd-section-head">
              <div className="spd-section-left">
                <div className="spd-section-title">Atamalar</div>
                <div className="spd-section-sub">
                  {prepared.length} kayıt gösteriliyor
                </div>
              </div>
            </div>

            {/* List */}
            <section className="spd-list spd-list--separated">
              {prepared.map((a) => {
                const assignedAtRaw =
                  getAssignedAt(a) || getApplicationCreatedAt(a);
                const appCreatedAtRaw = getApplicationCreatedAt(a);
                const thumbType = getCardThumbType(a);

                const feedbackChip = a.has_feedback
                  ? { className: "md-chip md-chip-success", label: "Tamamlandı" }
                  : { className: "md-chip md-chip-warning", label: "Bekliyor" };

                const appStatus = a?.application_detail?.status;
                const appStatusChip = mapApplicationStatusChip(appStatus);

                const filesCount = Array.isArray(a?.application_detail?.files)
                  ? a.application_detail.files.length
                  : 0;
                const docsCount = Array.isArray(a?.application_detail?.documents)
                  ? a.application_detail.documents.length
                  : 0;
                const attachmentsTotal = filesCount + docsCount;

                const hasLatLng = Boolean(
                  a?.application_detail?.latitude &&
                    a?.application_detail?.longitude
                );

                // Şimdilik detail yoksa #id göster
                const neighborhoodRaw =
  a?.application_detail?.neighborhood_detail?.name ??
  (a?.application_detail?.neighborhood ? `#${a.application_detail.neighborhood}` : null);

const neighborhood = titleCaseTr(safeText(neighborhoodRaw));

const unitRaw =
  a?.application_detail?.unit_detail?.name ??
  (a?.application_detail?.unit ? `#${a.application_detail.unit}` : null);

const unit = titleCaseTr(safeText(unitRaw));


                return (
                  <button
                    key={a.id}
                    type="button"
                    className={`md-card spd-item ${a.has_feedback ? "is-done" : "is-active"}`}
                    onClick={() => handleCardClick(a.id)}
                  >
                    <div className="spd-item-main">
                      <div className="spd-item-thumb" aria-hidden="true">
                        {thumbType === "image" ? (
                          <img
                            src={a.application_detail.files[0]}
                            alt=""
                            className="spd-item-thumb-img"
                          />
                        ) : thumbType === "location" ? (
                          <MapPinIcon width={20} height={20} />
                        ) : (
                          <DocumentTextIcon width={20} height={20} />
                        )}
                      </div>

                      <div className="spd-item-content">
                        <div className="spd-item-top">
                          <div className="spd-item-title">
                            {a?.application_detail?.tracking_number ? (
                              <span className="spd-item-tracking">
                                <HashtagIcon width={14} height={14} />
                                {a.application_detail.tracking_number}
                              </span>
                            ) : null}
                            <span className="spd-item-subject">
  {shorten(ensureFirstUpperTr(a?.application_detail?.subject), 90)}
</span>

                          </div>

                          <div className="spd-item-badges">
                            <span className={feedbackChip.className}>
                              {feedbackChip.label}
                            </span>
                            {appStatusChip ? (
                              <span className={appStatusChip.className}>
                                {appStatusChip.label}
                              </span>
                            ) : null}
                          </div>
                        </div>

                        <div className="spd-item-desc">
  {shorten(ensureFirstUpperTr(a?.application_detail?.description), 160)}
</div>


                        <div className="spd-item-meta">
                         <span className="spd-meta-pill">
  <CalendarDaysIcon width={16} height={16} />
  <b>Atama:</b> <TrDateTime value={assignedAtRaw} />
</span>


                         {appCreatedAtRaw ? (
  <span className="spd-meta-pill">
    <ClockIcon width={16} height={16} />
    <b>Başvuru:</b> <TrDateTime value={appCreatedAtRaw} mode="date" />
  </span>
) : null}


                          <span className="spd-meta-pill">
                            <BuildingOffice2Icon width={16} height={16} />
                            <b>Birim:</b> {unit}
                          </span>

                          <span className="spd-meta-pill">
                            <HomeModernIcon width={16} height={16} />
                            <b>Mahalle:</b> {neighborhood}
                          </span>

                          {attachmentsTotal > 0 ? (
                            <span className="spd-meta-pill">
                              <PaperClipIcon width={16} height={16} />
                              <b>Ek:</b> {attachmentsTotal}
                            </span>
                          ) : null}

                          {hasLatLng ? (
                            <span className="spd-meta-pill">
                              <MapPinIcon width={16} height={16} />
                              Konum
                            </span>
                          ) : null}

                          {a?.assignment_note ? (
  <span
    className="spd-meta-pill spd-meta-pill--note"
    title={ensureFirstUpperTr(a.assignment_note)}
  >
    <DocumentTextIcon width={16} height={16} />
    <b>Atama Notu:</b> {shorten(ensureFirstUpperTr(a.assignment_note), 60)}
  </span>
) : null}

                        </div>
                      </div>
                    </div>

                    <div className="spd-item-chevron" aria-hidden="true">
                      <ChevronRightIcon width={18} height={18} />
                    </div>
                  </button>
                );
              })}
            </section>
    </div>
  );
};

export default SahaPersoneliDashboardPage;
